
import { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import PartnerApplicationForm from '@/components/partner/PartnerApplicationForm';
import { Card, CardContent } from '@/components/ui/card';
import { Handshake, Users, TrendingUp, Mail } from 'lucide-react';
import { SEOMetaTags } from '@/components/SEOMetaTags';

const PartnerPage = () => {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);

  const handleApplyClick = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-[#121212] text-white">
      <SEOMetaTags 
        title="Werde CHRONA Partner | 25% Provision"
        description="Tritt unserem Partner-Programm bei und verdiene 25% Provision für jeden Studio-Kunden, den du uns vermittelst. Bewerbe dich jetzt!"
      />
      
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-[300px] -right-[300px] w-[600px] h-[600px] bg-chrona-accent/10 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-[200px] -left-[200px] w-[400px] h-[400px] bg-chrona-accent/5 rounded-full blur-2xl"></div>
        </div>
        
        <div className="container mx-auto px-4 relative">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-gradient">
              Werde offizieller CHRONA Partner
            </h1>
            <p className="text-lg md:text-xl text-gray-300 mb-8">
              Empfehle Studios oder teile CHRONA mit deiner Community – 
              erhalte <span className="text-chrona-accent font-semibold">25%</span> Provision auf jeden zahlenden Studio-Kunden.
            </p>
            <Button 
              onClick={handleApplyClick} 
              className="bg-chrona-accent text-white hover:bg-chrona-accent/90 px-8 py-6 h-auto text-lg"
            >
              Jetzt bewerben
            </Button>
          </motion.div>
        </div>
      </section>
      
      {/* Benefits Section */}
      <section className="py-16 bg-[#151515]">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center">Die Vorteile unseres Partnerprogramms</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <TrendingUp className="h-10 w-10 text-chrona-accent" />,
                title: "25% Provision",
                description: "Erhalte 25% der Abonnementgebühr für jeden Studio-Kunden, den du uns vermittelst - wiederkehrend für 12 Monate."
              },
              {
                icon: <Users className="h-10 w-10 text-chrona-accent" />,
                title: "Tracking-System",
                description: "Mit deinem persönlichen Partnerlink verfolgen wir alle deine Empfehlungen automatisch."
              },
              {
                icon: <Handshake className="h-10 w-10 text-chrona-accent" />,
                title: "Langfristige Partnerschaft",
                description: "Wir setzen auf langfristige Zusammenarbeit und unterstützen dich mit Marketingmaterialien."
              },
            ].map((benefit, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="bg-[#1A1A1A] p-6 rounded-lg border border-gray-800"
              >
                <div className="mb-4">{benefit.icon}</div>
                <h3 className="text-xl font-semibold mb-3">{benefit.title}</h3>
                <p className="text-gray-400">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Application Form Section */}
      <section className="py-16 bg-[#121212]" ref={formRef} id="apply">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl font-bold mb-4 text-center">Bewirb dich als Partner</h2>
            <p className="text-lg text-gray-300 mb-8 text-center max-w-2xl mx-auto">
              Fülle das Formular aus und wir melden uns innerhalb von 48 Stunden bei dir zurück.
            </p>
            
            {formSubmitted ? (
              <Card className="max-w-xl mx-auto bg-[#1A1A1A] border-green-500/30">
                <CardContent className="pt-6 text-center">
                  <div className="bg-green-500/10 p-3 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                    <Mail className="h-8 w-8 text-green-400" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Vielen Dank für deine Bewerbung!</h3>
                  <p className="text-gray-300">
                    Wir prüfen deinen Antrag manuell und melden uns zeitnah per E-Mail.
                    Falls du Fragen hast, kontaktiere uns unter <a href="mailto:partners@chrona.de" className="text-chrona-accent">partners@chrona.de</a>.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <Card className="max-w-xl mx-auto bg-[#1A1A1A] border-gray-800">
                <CardContent className="pt-6">
                  <PartnerApplicationForm 
                    onSuccess={() => setFormSubmitted(true)} 
                  />
                </CardContent>
              </Card>
            )}
          </motion.div>
        </div>
      </section>
      
      {/* FAQ Section */}
      <section className="py-16 bg-[#151515]">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center">Häufig gestellte Fragen</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {[
              {
                question: "Wie hoch ist die Provision?",
                answer: "Du erhältst 25% der monatlichen Abonnementgebühr für jeden Studio-Kunden, den du uns vermittelst. Die Provision wird 12 Monate lang ausgezahlt."
              },
              {
                question: "Wie erfolgt die Auszahlung?",
                answer: "Die Auszahlung erfolgt monatlich per Überweisung oder PayPal, sobald ein Mindestbetrag von 50€ erreicht ist."
              },
              {
                question: "Wer kann Partner werden?",
                answer: "Influencer, Bloggers, Studio-Besitzer oder jeder mit einer relevanten Community im Beauty- und Wellness-Bereich kann sich bewerben."
              },
              {
                question: "Wie lange dauert der Bewerbungsprozess?",
                answer: "In der Regel prüfen wir Bewerbungen innerhalb von 48 Stunden und melden uns dann per E-Mail."
              },
            ].map((faq, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                className="bg-[#1A1A1A] p-6 rounded-lg border border-gray-800"
              >
                <h3 className="text-xl font-semibold mb-3">{faq.question}</h3>
                <p className="text-gray-400">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default PartnerPage;
